import { HiOutlineArrowCircleRight } from 'react-icons/hi';
import { getSanityImageUrl } from '@/libs/sanity/client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { getPageById } from '@/libs/sanity/client';
import { Cover } from '@/components/ui/cover';
import Breadcrumb from '@/components/Breadcrumb';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import { PortableTextBlock } from '@sanity/types';
import type { ArbitraryTypedObject } from '@portabletext/types';
import BlurFadeText, {
  BLUR_FADE_DELAY,
} from '@/components/magicui/blur-fade-text';
import { BlurInDiv } from '@/components/eldora-ui/blur-in-div';
import { BlurInH1 } from './eldora-ui/blur-in-h1';
import { useMediaQuery } from 'usehooks-ts';
import { TABLET_SCREEN_SIZE, MOBILE_SCREEN_SIZE } from '@/utils/constants';
import { RainbowButton } from './ui/rainbow-button';
type HeroProps = {
  title?: string;
  subtitle?: string;
  description?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  image?: any;
  backgroundSize?: string;
  backgroundPosition?: string;
  ctas: Array<{
    link: {
      label?: string;
      type: string;
      _type: string;
      internal?: {
        _ref?: string;
        ref?: any;
      };
      external?: string;
    };
  }>;
};

const SimplicityCover = ({ text }: { text: string }) => {
  const [firstText, restText] = text.split('Simplicity®');
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);

  if (restText === undefined) {
    return (
      <BlurInH1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl !leading-[1.35em] mt-8 mb-8">
        <BlurFadeText duration={BLUR_FADE_DELAY} text={firstText} />
      </BlurInH1>
    );
  }
  return !isTablet ? (
    <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl !leading-[1.35em] my-8">
      <div className="flex space-x-5 items-baseline">
        {firstText && (
          <BlurFadeText duration={BLUR_FADE_DELAY} text={`${firstText} `} />
        )}
        <Cover>
          <span className="font-semibold">
            Simplicity<sup>®</sup>
          </span>
        </Cover>{' '}
      </div>
      <BlurFadeText duration={BLUR_FADE_DELAY} text={restText} />
    </h1>
  ) : (
    <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl !leading-[1.35em] mt-2">
      <BlurFadeText duration={BLUR_FADE_DELAY} text={`${text} `} />
    </h1>
  );
};

const Hero = ({
  title,
  image,
  description,
  ctas,
  backgroundSize = 'bg-contain',
  backgroundPosition = 'bg-right-bottom',
}: HeroProps) => {
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const cta = ctas[0];
  const ctaLabel = cta.link.label;
  const isExternal = cta.link.external !== undefined;
  const ctaUrl = cta.link.external ?? cta.link.internal._ref;
  const [internalRef, setInternalRef] = useState<string>('#');

  useEffect(() => {
    const fetchData = async () => {
      if (cta.link.internal) {
        cta.link.internal.ref = await getPageById(cta.link.internal._ref);
        setInternalRef(cta.link.internal.ref?.metadata?.slug?.current);
      }
    };

    fetchData();
  }, []);

  return (
    <section className="bg-lightGray flex max-w-8xl mx-auto mb-4 relative">
      <div className="w-full flex flex-col lg:flex-row md:min-h-72 lg:min-h-96 pt-8 lg:pt-12 relative">
        <div
          className={`absolute inset-0 bg-no-repeat ${backgroundPosition} ${backgroundSize}`}
          style={{
            backgroundImage: isMobile
              ? 'none'
              : `url('${getSanityImageUrl(image)}')`,
            filter: isMobile ? 'blur(1.5px)' : 'none',
            backgroundPosition: backgroundPosition,
            backgroundSize: backgroundSize,
          }}
        />
        <div className="sm:max-w-[55%] flex flex-col xl:gap-10 xl:justify-between mb-8 lg:mb-12 lg:text-left lg:min-h-96 px-4 relative">
          <div>
            <Breadcrumb />
            <SimplicityCover text={title || ''} />
            <BlurInDiv className="mt-4 text-lg max-w-[90%]">
              {description && <PortableTextWrapper value={description} />}
            </BlurInDiv>
          </div>
          <RainbowButton className="text-[12px] xl:text-base max-w-[150px] lg:max-w-[200px] flex flex-row justify-between align-baseline !px-3">
            {isExternal ? (
              <Link
                href={ctaUrl}
                target={cta.link.external ? '_blank' : undefined}
              >
                {ctaLabel}
              </Link>
            ) : (
              <Link href={'/' + internalRef}>{ctaLabel}</Link>
            )}
            <HiOutlineArrowCircleRight
              className="text-white text-sm xl:text-lg"
              size="22"
            />
          </RainbowButton>
        </div>
      </div>
    </section>
  );
};

export default Hero;
